/**
 * EVENTSFLO-PUBLIC-QUOTE-AUTO-CLIENT-VENUE-MATCH-SUGGESTIONS-V1
 *
 * Admin review surface for public quote requests with client/venue
 * match suggestions.
 *
 * SUGGESTION-ONLY: this page does NOT create or modify any client,
 * contact, user, venue, or project. The "Save selection" button only
 * stores admin's selection as metadata on the public quote request row.
 */

import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Loader2, FileText, AlertCircle, ShieldCheck, ArrowLeft, FilePlus2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
// EVENTSFLO-GUIDE-BUTTON-ROLLOUT-V1
import { GuideButton } from "../components/common/GuideButton";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "eventsflo_auth_token";

function authHeaders() {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function ConfidenceBadge({ value }) {
  const map = {
    high: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    medium: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    low: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  };
  return (
    <Badge
      variant="outline"
      className={`${map[value] || map.low}`}
      data-testid={`confidence-${value}`}
    >
      {value}
    </Badge>
  );
}

function PqrListView({ items, onSelect }) {
  if (!items.length) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="pqr-empty">
        No public quote requests yet.
      </p>
    );
  }
  return (
    <div className="space-y-2" data-testid="pqr-list">
      {items.map((row) => (
        <button
          key={row.id}
          onClick={() => onSelect(row.id)}
          className="w-full text-left rounded-md border border-border/40 hover:border-primary/50 hover:bg-primary/5 transition-colors px-3 py-2"
          data-testid={`pqr-list-item-${row.id}`}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">
                {row.project_name || row.venue || "(no name)"}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {row.requester_name} · {row.requester_email}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge
                variant="outline"
                className="text-[10px]"
                data-testid={`pqr-status-${row.id}`}
              >
                {row.match_review_status || "pending"}
              </Badge>
              <div className="text-xs text-muted-foreground">
                {row.start_date || ""}
              </div>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function ReasonChips({ reasons }) {
  if (!reasons?.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {reasons.map((r) => (
        <span
          key={r}
          className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20"
        >
          {r}
        </span>
      ))}
    </div>
  );
}

function ClientSuggestionCard({ s, selected, onSelect }) {
  return (
    <button
      onClick={() => onSelect(s.client_id)}
      className={`w-full text-left rounded-md border px-3 py-2 transition-colors ${
        selected
          ? "border-primary bg-primary/10"
          : "border-border/40 hover:border-primary/50"
      }`}
      data-testid={`client-suggestion-${s.client_id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate">{s.client_name}</div>
          <div className="text-[11px] text-muted-foreground">id: {s.client_id}</div>
          <ReasonChips reasons={s.reasons} />
          {s.risk_flags?.length > 0 && (
            <div className="text-[10px] text-amber-300 mt-1">
              ⚠ {s.risk_flags.join(", ")}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <ConfidenceBadge value={s.confidence} />
          <span className="text-[10px] text-muted-foreground">score {s.score}</span>
        </div>
      </div>
    </button>
  );
}

function VenueSuggestionCard({ s, selected, onSelect }) {
  const actionLabel = {
    use_existing_visible_venue: "Use existing (visible to client)",
    share_existing_venue_with_client: "SHARE tenant venue with client",
    copy_existing_venue_to_client: "COPY venue to client",
    do_not_share_other_client_private: "⚠ Other client's private venue — do NOT share",
    create_new_venue_review_required: "Create new venue (admin review)",
  }[s.recommended_action] || s.recommended_action;

  return (
    <button
      onClick={() => onSelect(s.venue_id)}
      className={`w-full text-left rounded-md border px-3 py-2 transition-colors ${
        selected
          ? "border-primary bg-primary/10"
          : "border-border/40 hover:border-primary/50"
      }`}
      data-testid={`venue-suggestion-${s.venue_id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate">{s.venue_name}</div>
          <div className="text-[11px] text-muted-foreground">
            visibility: {s.visibility_type || "—"}{" "}
            {s.is_client_visible_for_candidate_client && " · visible to client"}
          </div>
          <ReasonChips reasons={s.reasons} />
          <div className="text-[11px] text-foreground mt-1">{actionLabel}</div>
          {s.rationale && (
            <div className="text-[10px] text-muted-foreground mt-0.5">
              {s.rationale}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <ConfidenceBadge value={s.confidence} />
          <span className="text-[10px] text-muted-foreground">score {s.score}</span>
        </div>
      </div>
    </button>
  );
}

function ActionButtons({ requestId, data, pickedClient, pickedVenue, onApplied }) {
  const [busy, setBusy] = useState("");
  const [confirmDialog, setConfirmDialog] = useState(null);
  // confirmDialog shape: { type, title, body, payload }

  const review = data.review_metadata || {};
  const venueSel = data.venue_suggestions?.find((v) => v.venue_id === pickedVenue);

  async function callAction(actionPath, body) {
    setBusy(actionPath);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/public-quote-requests/${requestId}/actions/${actionPath}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify(body || {}),
        }
      );
      const out = await r.json().catch(() => ({}));
      if (!r.ok) {
        const detail =
          typeof out.detail === "string"
            ? out.detail
            : out.detail?.message || JSON.stringify(out.detail || out);
        throw new Error(detail || `HTTP ${r.status}`);
      }
      toast.success(
        `${actionPath} applied${
          out.idempotent_replay ? " (idempotent replay)" : ""
        }.`
      );
      onApplied?.();
    } catch (e) {
      toast.error(`${actionPath} failed: ${e.message}`);
    } finally {
      setBusy("");
      setConfirmDialog(null);
    }
  }

  function openConfirm(type) {
    if (type === "link-client") {
      if (!pickedClient) return toast.error("Pick a client suggestion first.");
      setConfirmDialog({
        type,
        title: "Link this request to the selected client?",
        body:
          "This updates only the public quote request metadata. " +
          "It does NOT create a portal user, project, quote, or invoice.",
        payload: { client_id: pickedClient, confirm_change: true },
      });
    } else if (type === "create-client") {
      setConfirmDialog({
        type,
        title: "Create a new client from this request?",
        body:
          "This creates a CLIENT record only, populated from the request " +
          "draft. It will NOT create a portal user, contact invite, " +
          "project, quote, or invoice. If a similar client exists, you " +
          "will be asked for an override reason.",
        payload: { confirm_create_client: true },
      });
    } else if (type === "link-venue") {
      if (!pickedVenue) return toast.error("Pick a venue suggestion first.");
      setConfirmDialog({
        type,
        title: "Link this request to the selected venue?",
        body:
          "This only updates metadata. The venue must already be " +
          "visible to the matched client.",
        payload: { venue_id: pickedVenue },
      });
    } else if (type === "share-venue") {
      if (!pickedVenue || !pickedClient)
        return toast.error("Pick a client and a venue first.");
      setConfirmDialog({
        type,
        title: "Share this venue with the selected client?",
        body:
          "This shares the SAME canonical venue/location IDs with the " +
          "client. No new venue is created. Editing the venue later " +
          "affects everyone it is shared with.",
        payload: {
          venue_id: pickedVenue,
          client_id: pickedClient,
          confirm_share: true,
        },
      });
    } else if (type === "copy-venue-to-client") {
      if (!pickedVenue || !pickedClient)
        return toast.error("Pick a client and a venue first.");
      setConfirmDialog({
        type,
        title: "Copy this venue to the selected client?",
        body:
          "This creates a NEW, EDITABLE venue (and its active locations) " +
          "owned by the client. The source venue is unchanged. No " +
          "project/quote/invoice is created.",
        payload: {
          venue_id: pickedVenue,
          client_id: pickedClient,
          confirm_copy: true,
        },
      });
    }
  }

  const clientApplied = !!review.client_action_status;
  const venueApplied = !!review.venue_action_status;

  return (
    <div className="space-y-3" data-testid="action-buttons-block">
      <div className="flex flex-wrap gap-2">
        <Button
          variant={clientApplied && review.client_action_status === "linked_existing" ? "secondary" : "default"}
          size="sm"
          disabled={!pickedClient || busy !== ""}
          onClick={() => openConfirm("link-client")}
          data-testid="action-link-client-btn"
        >
          {busy === "link-client" ? (
            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
          ) : null}
          Link to selected client
        </Button>
        <Button
          variant={clientApplied && review.client_action_status === "created_new" ? "secondary" : "outline"}
          size="sm"
          disabled={busy !== ""}
          onClick={() => openConfirm("create-client")}
          data-testid="action-create-client-btn"
        >
          {busy === "create-client" ? (
            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
          ) : null}
          Create new client from draft
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          variant={venueApplied && review.venue_action_status === "linked_existing_visible" ? "secondary" : "default"}
          size="sm"
          disabled={!pickedVenue || busy !== ""}
          onClick={() => openConfirm("link-venue")}
          data-testid="action-link-venue-btn"
        >
          {busy === "link-venue" ? (
            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
          ) : null}
          Use selected venue (already visible)
        </Button>
        <Button
          variant={venueApplied && review.venue_action_status === "shared_existing" ? "secondary" : "outline"}
          size="sm"
          disabled={!pickedVenue || !pickedClient || busy !== ""}
          onClick={() => openConfirm("share-venue")}
          data-testid="action-share-venue-btn"
        >
          {busy === "share-venue" ? (
            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
          ) : null}
          Share venue with client
        </Button>
        <Button
          variant={venueApplied && review.venue_action_status === "copied_to_client" ? "secondary" : "outline"}
          size="sm"
          disabled={!pickedVenue || !pickedClient || busy !== ""}
          onClick={() => openConfirm("copy-venue-to-client")}
          data-testid="action-copy-venue-btn"
        >
          {busy === "copy-venue-to-client" ? (
            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
          ) : null}
          Copy venue to client
        </Button>
      </div>

      {venueSel?.recommended_action === "do_not_share_other_client_private" && (
        <Alert variant="destructive" data-testid="venue-private-warning">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-xs">
            Selected venue is private to another client. Sharing is blocked
            by default for safety.
          </AlertDescription>
        </Alert>
      )}

      {confirmDialog && (
        <ConfirmActionDialog
          open
          title={confirmDialog.title}
          body={confirmDialog.body}
          actionType={confirmDialog.type}
          payload={confirmDialog.payload}
          onCancel={() => setConfirmDialog(null)}
          onConfirm={(finalPayload) =>
            callAction(confirmDialog.type, finalPayload)
          }
          busy={busy !== ""}
        />
      )}
    </div>
  );
}

function ConfirmActionDialog({
  open,
  title,
  body,
  actionType,
  payload,
  onCancel,
  onConfirm,
  busy,
}) {
  const [overrideReason, setOverrideReason] = useState("");

  if (!open) return null;
  const isCreateClient = actionType === "create-client";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      data-testid="confirm-action-dialog"
    >
      <Card className="w-full max-w-md mx-4">
        <CardHeader>
          <CardTitle className="text-base">{title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p className="text-muted-foreground">{body}</p>
          {isCreateClient && (
            <div>
              <label className="text-xs text-muted-foreground">
                Optional: duplicate override reason
              </label>
              <input
                type="text"
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                className="w-full mt-1 bg-background border border-border/40 rounded-md px-2 py-1 text-sm"
                data-testid="dialog-override-reason"
                placeholder="(only needed if duplicate detected)"
              />
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={onCancel}
              data-testid="dialog-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={busy}
              onClick={() =>
                onConfirm(
                  isCreateClient && overrideReason
                    ? { ...payload, override_duplicate_reason: overrideReason }
                    : payload
                )
              }
              data-testid="dialog-confirm-btn"
            >
              {busy ? (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              ) : null}
              Confirm
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// EVENTSFLO-PUBLIC-QUOTE-TO-DRAFT-PROJECT-REQUEST-V1
// Admin-only card that creates a DRAFT Client Project Request from a
// reviewed Public Quote Request. Idempotent. Does NOT create a live
// project / shift / quote / invoice / portal user. Does NOT send email.
function DraftProjectRequestCard({ requestId, review, onApplied }) {
  const [open, setOpen] = useState(false);
  const [adminNote, setAdminNote] = useState("");
  const [allowTbdVenue, setAllowTbdVenue] = useState(false);
  const [busy, setBusy] = useState(false);

  const clientReady =
    !!review.matched_client_id || !!review.created_client_id;
  const venueReady =
    !!review.matched_venue_id || !!review.created_client_venue_id;
  const alreadyCreated = !!review.created_client_project_request_id;
  const createdId = review.created_client_project_request_id || null;

  async function callCreate(payload) {
    setBusy(true);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/public-quote-requests/${requestId}/actions/create-draft-project-request`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify(payload),
        }
      );
      const out = await r.json().catch(() => ({}));
      if (!r.ok) {
        const detail =
          typeof out.detail === "string"
            ? out.detail
            : out.detail?.message || JSON.stringify(out.detail || out);
        throw new Error(detail || `HTTP ${r.status}`);
      }
      toast.success(
        out.idempotent_replay
          ? "Draft project request already exists (idempotent replay)."
          : "Draft project request created. Open it in Client Requests to review."
      );
      setOpen(false);
      onApplied?.();
    } catch (e) {
      toast.error(`create-draft-project-request failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  }

  function onConfirm() {
    callCreate({
      confirm_create_draft: true,
      allow_tbd_venue: allowTbdVenue,
      admin_note: (adminNote || "").trim() || undefined,
    });
  }

  return (
    <Card data-testid="draft-project-request-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <FilePlus2 className="h-4 w-4 text-primary" />
          Create draft project request
        </CardTitle>
        <CardDescription className="text-xs">
          Hands the reviewed request off to Admin → Client Requests as a
          draft. Does not create a live project, shifts, quote, invoice,
          or send any email.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {alreadyCreated ? (
          <div
            className="rounded-md bg-emerald-500/10 border border-emerald-500/30 p-3 flex items-start gap-3"
            data-testid="draft-project-request-already-created"
          >
            <ShieldCheck className="h-4 w-4 text-emerald-300 mt-0.5" />
            <div className="flex-1 text-emerald-100 text-xs">
              <div className="font-medium">Already created</div>
              <div className="text-emerald-200/80 mt-0.5">
                A draft Client Project Request was created from this PQR.
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Badge
                  variant="outline"
                  className="bg-emerald-500/20 border-emerald-400/40 text-emerald-100 font-mono text-[10px]"
                  data-testid="draft-project-request-id-badge"
                >
                  {createdId.slice(0, 8)}…
                </Badge>
                <Link
                  to={`/client-requests?highlight=${encodeURIComponent(createdId)}`}
                  className="inline-flex items-center gap-1 text-xs underline text-emerald-200 hover:text-emerald-100"
                  data-testid="draft-project-request-open-link"
                >
                  Open in Client Requests
                  <ExternalLink className="h-3 w-3" />
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <>
            {!clientReady && (
              <Alert
                variant="destructive"
                data-testid="draft-project-request-client-warning"
              >
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  Confirm or create a client before creating a draft project
                  request.
                </AlertDescription>
              </Alert>
            )}
            {clientReady && !venueReady && (
              <Alert
                className="bg-amber-500/10 border-amber-500/30 text-amber-200"
                data-testid="draft-project-request-venue-warning"
              >
                <AlertCircle className="h-4 w-4 text-amber-300" />
                <AlertDescription className="text-xs">
                  No venue is confirmed. You can still create the draft with a
                  TBD venue — admin will resolve it during the convert flow.
                </AlertDescription>
              </Alert>
            )}

            <Button
              variant="default"
              size="sm"
              disabled={!clientReady || busy}
              onClick={() => setOpen(true)}
              data-testid="action-create-draft-project-request-btn"
            >
              {busy ? (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              ) : (
                <FilePlus2 className="h-3.5 w-3.5 mr-1" />
              )}
              Create draft project request
            </Button>

            {open && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
                data-testid="create-draft-confirm-dialog"
              >
                <Card className="w-full max-w-md mx-4">
                  <CardHeader>
                    <CardTitle className="text-base">
                      Create a draft Client Project Request?
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <p className="text-muted-foreground">
                      This creates a draft Client Project Request only. It
                      will not create a live project, shifts, quote, invoice,
                      or send email.
                    </p>
                    {!venueReady && (
                      <label
                        className="flex items-start gap-2 text-xs text-amber-200"
                        data-testid="create-draft-allow-tbd-row"
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          checked={allowTbdVenue}
                          onChange={(e) => setAllowTbdVenue(e.target.checked)}
                          data-testid="create-draft-allow-tbd-checkbox"
                        />
                        <span>
                          No venue is confirmed. Tick to proceed with a TBD
                          venue — admin will resolve it during the convert
                          flow.
                        </span>
                      </label>
                    )}
                    <div>
                      <label className="text-xs text-muted-foreground">
                        Optional admin note
                      </label>
                      <input
                        type="text"
                        value={adminNote}
                        onChange={(e) => setAdminNote(e.target.value)}
                        className="w-full mt-1 bg-background border border-border/40 rounded-md px-2 py-1 text-sm"
                        placeholder="(visible to admin only)"
                        data-testid="create-draft-admin-note"
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        onClick={() => setOpen(false)}
                        data-testid="create-draft-dialog-cancel"
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        disabled={
                          busy || (!venueReady && !allowTbdVenue)
                        }
                        onClick={onConfirm}
                        data-testid="create-draft-dialog-confirm"
                      >
                        {busy ? (
                          <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        ) : null}
                        Create draft
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}


function PqrDetailView({ requestId, onBack, onReviewed }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const [pickedClient, setPickedClient] = useState(null);
  const [pickedVenue, setPickedVenue] = useState(null);
  const [pickedAction, setPickedAction] = useState(null);
  const [pickedStatus, setPickedStatus] = useState("reviewed");

  const reloadDetail = () => {
    setReloadKey((k) => k + 1);
    onReviewed?.();
  };

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const r = await fetch(
          `${API_URL}/api/admin/public-quote-requests/${requestId}/match-suggestions`,
          { headers: authHeaders() }
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const body = await r.json();
        if (!cancelled) {
          setData(body);
          setPickedClient(body.review_metadata?.matched_client_id || null);
          setPickedVenue(body.review_metadata?.matched_venue_id || null);
          setPickedAction(body.review_metadata?.intended_venue_action || null);
          setPickedStatus(body.review_metadata?.match_review_status || "reviewed");
        }
      } catch (e) {
        if (!cancelled) setError(String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [requestId, reloadKey]);

  async function handleSave() {
    setSaving(true);
    try {
      const payload = {
        matched_client_id: pickedClient,
        matched_venue_id: pickedVenue,
        intended_venue_action: pickedAction,
        match_review_status: pickedStatus,
      };
      // Bucket the confidences from the picked suggestion
      const cs = data?.client_suggestions?.find((s) => s.client_id === pickedClient);
      const vs = data?.venue_suggestions?.find((s) => s.venue_id === pickedVenue);
      if (cs) payload.matched_client_confidence = cs.confidence;
      if (vs) payload.matched_venue_confidence = vs.confidence;
      const r = await fetch(
        `${API_URL}/api/admin/public-quote-requests/${requestId}/match-review`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify(payload),
        }
      );
      if (!r.ok) {
        const detail = await r.json().catch(() => ({}));
        throw new Error(detail.detail || `HTTP ${r.status}`);
      }
      toast.success("Selection saved as suggestion metadata. No data was mutated.");
      onReviewed?.();
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8" data-testid="pqr-detail-loading">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  }
  if (error) {
    return (
      <Alert variant="destructive" data-testid="pqr-detail-error">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="pqr-detail">
      <Button
        variant="ghost"
        size="sm"
        onClick={onBack}
        data-testid="pqr-back-btn"
      >
        <ArrowLeft className="h-4 w-4 mr-1" /> Back to list
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Request summary
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          <div><span className="text-muted-foreground">Requester:</span> {data.summary.requester_name}</div>
          <div><span className="text-muted-foreground">Company:</span> {data.summary.requester_company || "—"}</div>
          <div><span className="text-muted-foreground">Email:</span> {data.summary.requester_email}</div>
          <div><span className="text-muted-foreground">Existing client?:</span> {data.summary.existing_client || "—"}</div>
          <div><span className="text-muted-foreground">Venue:</span> {data.summary.venue || "—"}</div>
          <div><span className="text-muted-foreground">Venue address:</span> {data.summary.venue_address || "—"}</div>
          <div><span className="text-muted-foreground">Start:</span> {data.summary.start_date || "—"}</div>
          <div><span className="text-muted-foreground">Shifts:</span> {data.summary.shift_count ?? "—"}</div>
        </CardContent>
      </Card>

      <Alert
        className="bg-emerald-500/10 border-emerald-500/30 text-emerald-200"
        data-testid="pqr-mutation-proof"
      >
        <ShieldCheck className="h-4 w-4 text-emerald-300" />
        <AlertDescription className="text-xs">
          Suggestion-only. This screen does NOT create or modify any client,
          venue, project, quote, or invoice. Saving stores only metadata on
          the public quote request.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Client matches</CardTitle>
          <CardDescription className="text-xs">
            {data.client_global_risks?.length
              ? `⚠ ${data.client_global_risks.join(", ")}`
              : "Click to pick the intended client."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {data.client_suggestions.length === 0 ? (
            <p
              className="text-sm text-muted-foreground"
              data-testid="client-suggestions-empty"
            >
              No client match. Admin should review the prefilled draft and
              create a new client manually.
            </p>
          ) : (
            data.client_suggestions.map((s) => (
              <ClientSuggestionCard
                key={s.client_id}
                s={s}
                selected={pickedClient === s.client_id}
                onSelect={setPickedClient}
              />
            ))
          )}
          {data.new_client_draft?.name && (
            <details className="text-xs text-muted-foreground mt-2">
              <summary className="cursor-pointer">New-client draft (prefilled, NOT created)</summary>
              <pre className="mt-1 p-2 bg-muted/30 rounded text-[10px] overflow-x-auto">
                {JSON.stringify(data.new_client_draft, null, 2)}
              </pre>
            </details>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Venue matches</CardTitle>
          <CardDescription className="text-xs">
            {data.venue_global_risks?.length
              ? `⚠ ${data.venue_global_risks.join(", ")}`
              : "Click to pick the intended venue + action."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {data.venue_suggestions.length === 0 ? (
            <p
              className="text-sm text-muted-foreground"
              data-testid="venue-suggestions-empty"
            >
              No venue match. Admin should create a new venue after review.
            </p>
          ) : (
            data.venue_suggestions.map((s) => (
              <VenueSuggestionCard
                key={s.venue_id}
                s={s}
                selected={pickedVenue === s.venue_id}
                onSelect={(vid) => {
                  setPickedVenue(vid);
                  setPickedAction(s.recommended_action);
                }}
              />
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recommended next actions</CardTitle>
          <CardDescription className="text-xs">
            Each action below requires explicit confirmation and never
            creates a project, shift, quote, invoice, or portal user.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="text-sm space-y-2 mb-4" data-testid="recommended-actions">
            {data.recommended_actions.map((a, i) => (
              <li key={i}>
                <span className="font-mono text-[11px] text-primary">{a.action}</span>
                <div className="text-xs text-muted-foreground">{a.rationale}</div>
              </li>
            ))}
          </ul>

          {/* Applied-status badges */}
          <div className="flex flex-wrap gap-2 mb-3" data-testid="applied-status-badges">
            {data.review_metadata?.client_action_status && (
              <Badge variant="outline" className="bg-emerald-500/15 border-emerald-500/30 text-emerald-300" data-testid="badge-client-action">
                Client: {data.review_metadata.client_action_status}
              </Badge>
            )}
            {data.review_metadata?.venue_action_status && (
              <Badge variant="outline" className="bg-emerald-500/15 border-emerald-500/30 text-emerald-300" data-testid="badge-venue-action">
                Venue: {data.review_metadata.venue_action_status}
              </Badge>
            )}
          </div>

          <ActionButtons
            requestId={requestId}
            data={data}
            pickedClient={pickedClient}
            pickedVenue={pickedVenue}
            onApplied={reloadDetail}
          />
        </CardContent>
      </Card>

      {/* EVENTSFLO-PUBLIC-QUOTE-TO-DRAFT-PROJECT-REQUEST-V1 */}
      <DraftProjectRequestCard
        requestId={requestId}
        review={data.review_metadata || {}}
        onApplied={reloadDetail}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Save selection</CardTitle>
          <CardDescription className="text-xs">
            Stores the admin's intended client/venue/action as metadata. No
            mutation of operational data.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <span className="text-muted-foreground">Picked client:</span>{" "}
              <span data-testid="picked-client">{pickedClient || "—"}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Picked venue:</span>{" "}
              <span data-testid="picked-venue">{pickedVenue || "—"}</span>
            </div>
            <div className="sm:col-span-2">
              <span className="text-muted-foreground">Intended venue action:</span>{" "}
              <span data-testid="picked-action">{pickedAction || "—"}</span>
            </div>
            <div>
              <label className="text-muted-foreground text-xs">Review status</label>
              <select
                className="block w-full bg-background border border-border/40 rounded-md px-2 py-1 mt-1 text-sm"
                value={pickedStatus}
                onChange={(e) => setPickedStatus(e.target.value)}
                data-testid="picked-status-select"
              >
                <option value="pending">pending</option>
                <option value="reviewed">reviewed</option>
                <option value="needs_new_client">needs_new_client</option>
                <option value="needs_new_venue">needs_new_venue</option>
              </select>
            </div>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-selection-btn"
            className="mt-2"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…
              </>
            ) : (
              "Save selection (metadata only)"
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default function AdminPublicQuoteRequestsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  async function loadList() {
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`${API_URL}/api/admin/public-quote-requests`, {
        headers: authHeaders(),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const body = await r.json();
      setItems(body.items || []);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadList();
  }, []);

  return (
    <div className="space-y-4" data-testid="admin-public-quote-requests-page">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Public Quote Requests
          </h1>
          <p className="text-xs text-muted-foreground">
            Review public crew requests and pick the intended client/venue.
            Suggestion-only — no operational data is mutated.
          </p>
        </div>
        {/* EVENTSFLO-GUIDE-BUTTON-ROLLOUT-V1 */}
        <GuideButton testId="public-quote-requests-guide-btn" />
      </div>

      {selectedId ? (
        <PqrDetailView
          requestId={selectedId}
          onBack={() => setSelectedId(null)}
          onReviewed={() => loadList()}
        />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">All requests</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            ) : error ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : (
              <PqrListView items={items} onSelect={setSelectedId} />
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
